import { kioskLocation } from "../App/kioskRoutes";
import { useEffect, useState } from "react";
import Backend from "../utils/api.js";
import "../utils/default.css";
import PropTypes from "prop-types";
import Validators from "../utils/validators.jsx";
import { useHistory } from "react-router-dom";
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  HStack,
  Stack,
  Button,
  Heading,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";

const REGISTRATION_FALLBACKS = {
  400: "Check your details and try again.",
  401: "Your sign-in expired. Sign in again to finish registering.",
  409: "An account already uses this email or directory ID. Ask a space manager for help.",
};

// 400 (invalid details), 401 (sign-in expired), 403 (another person's identity),
// 409 (email or directory ID already used) and network failures (status 0) carry
// their own explanation; anything else gets a generic retry prompt.
export function registrationErrorMessage(res) {
  const explained = [0, 400, 401, 403, 409].includes(res?.status);
  return (explained && res.message) || REGISTRATION_FALLBACKS[res?.status]
    || "Your account could not be created. Please try again.";
}

/*
 * This component is used to register a new user.
 */
export default function RegisterForm({ UID, routeFunc, onSubmit }) {
  const [isLoading, setIsLoading] = useState(false);
  const [userState, setUserState] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const history = useHistory();

  useEffect(() => {
    Backend.getUserInfoFromUID(UID).then((userInfoResult) => {
      setUserState(userInfoResult); // autofill form with user info pulled from LDAP
    });
  }, [UID]);

  return (
    <Flex
      minH={"100vh"}
      align={"center"}
      justify={"center"}
      bg={useColorModeValue("gray.50", "gray.800")}
    >
      <form
        onSubmit={(x) => {
          setIsLoading(true);
          setSubmitError(null);
          x.preventDefault();
          Backend.newUser({
            UID: UID,
            args: {
              ...userState,
              swipeNum: 0,
            }
          }) // No card yet: the next swipe fills it in.
            .then((res) => {
              if (res.status !== 200 && res.status !== 201) {
                setSubmitError(registrationErrorMessage(res));
                setIsLoading(false);
                return undefined;
              }
              // Re-fetch the full user record from the DB so downstream
              // pages (CustomReasons) have all fields including swipeNum.
              return Backend.getUser(UID)
                .then((fullUser) => {
                  if (fullUser.status === 200) {
                    onSubmit(fullUser);
                  } else {
                    onSubmit({ ...userState, UID, swipeNum: 0 });
                  }
                  history.push(kioskLocation(routeFunc(res), history.location));
                })
                .catch(() => {
                  onSubmit({ ...userState, UID, swipeNum: 0 });
                  history.push(kioskLocation(routeFunc(res), history.location));
                });
            })
            .catch(() => {
              setSubmitError(registrationErrorMessage(undefined));
              setIsLoading(false);
            });
        }}
      >
        <Stack spacing={8} mx={"auto"} maxW={"lg"} py={12} px={6}>
          <Stack align={"center"}>
            <Heading fontSize={"4xl"} textAlign={"center"}>
              Welcome to Pinpoint
            </Heading>
            <Text fontSize={"lg"} color={"gray.600"}>
              Confirm your details to create your account
            </Text>
          </Stack>
          <Box
            rounded={"lg"}
            bg={useColorModeValue("white", "gray.700")}
            boxShadow={"md"}
            p={8}
          >
            <Stack spacing={4}>
              <HStack>
                <Box>
                  <FormControl id="firstName" isRequired>
                    <FormLabel>First Name</FormLabel>
                    <Input
                      type="text"
                      value={userState.firstName}
                      onChange={(e) => {
                        Validators.constrainNameInput(e.target.value) &&
                          setUserState({
                            ...userState,
                            firstName: e.target.value,
                          });
                      }}
                      isInvalid={userState.firstName != null && !Validators.validateName(userState.firstName)}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <FormControl id="lastName" isRequired>
                    <FormLabel>Last Name</FormLabel>
                    <Input
                      type="text"
                      value={userState.lastName}
                      onChange={(e) => {
                        Validators.constrainNameInput(e.target.value) &&
                          setUserState({
                            ...userState,
                            lastName: e.target.value,
                          });
                      }}
                      isInvalid={userState.lastName != null && !Validators.validateName(userState.lastName)}
                    />
                  </FormControl>
                </Box>
              </HStack>
              <FormControl id="email" isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={userState.email}
                  onChange={(e) => {
                    setUserState({
                      ...userState,
                      email: e.target.value,
                    });
                  }}
                  isInvalid={userState.email != null && !Validators.validateEmail(userState.email)}
                />
              </FormControl>
              <FormControl id="directoryID" isRequired>
                <FormLabel>Directory ID</FormLabel>
                <Input type="text" value={userState.directoryID} disabled={true} />
              </FormControl>
              <FormControl id="UID" isRequired>
                <FormLabel>UID</FormLabel>
                <Input type="text" value={userState.UID} disabled={true} />
              </FormControl>
              {submitError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}
              <Stack spacing={10} pt={2}>
                <Button
                  isLoading={isLoading}
                  loadingText={"Creating Account..."}
                  fontFamily={"heading"}
                  type="submit"
                  w="full"
                  size="lg"
                >
                  Create Account
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </form>
    </Flex>
  );
}

RegisterForm.propTypes = {
  UID: PropTypes.number,
};
